import { type MutableRefObject } from 'react';

import { type Country } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import { type OddsFormat } from '~/types';

export type CallbackFn = (...args: unknown[]) => void;

export type OddsFormatIs = {
	American: boolean;
	Decimal: boolean;
	Fractional: boolean;
};

export type BetslipProviderProps = {
	userOddsFormat: OddsFormat;
};

export type BetslipEntry = {
	betId: number;
	odds?: number;
	selectedBookie: string;
};

export type Betslip = Record<string, BetslipEntry>;

export const betslipType = {
	SINGLE: 'single',
	MULTIPLE: 'multiple',
};

export type BetslipType = (typeof betslipType)[keyof typeof betslipType];

export type BookieOdds = Pick<
	Odds,
	| 'betIds'
	| 'bookmakerCode'
	| 'bookmakerType'
	| 'oddsDecimal'
	| 'oddsFractional'
	| 'oddsUs'
> & { index?: number };

export type BetslipUtils = {
	calculateOddsAndBestBookie: (data: Bet[], type: BetslipType) => BookieOdds[];
	getBestOdds: (bet: any) => string;
	getBestOddsFromBet: (odds: Odds[]) => {
		bestBookie?: string;
		bestOddsDecimal?: number;
		bestOddsFractional?: string;
		bestOddsUs?: number;
	};
	getBetMeta: (bet: any) => {
		betId: number;
		betName: string;
		marketName: string;
		spread: string | number;
		subeventName: string;
	};
	getBetSelectedBookieFromLocalStore: (betId: string) => string | null;
	getBetslipAPI: (
		betIds: number[],
		countryCode: Country,
		state: StateCode | '',
		completion: (success: any, res: any) => void,
	) => void;
	getBetslipCookieArray: () => [string, string][];
	getLocalStorageBetslip: () => Betslip;
	getOdds: (odds: any, opening?: boolean) => string;
	trackBetSlip: (override: boolean, betSlip: any[]) => void;
};

export type OddsUtils = {
	changeOddsFormat: (newOddsFormat: OddsFormat) => void;
	convertAmericanToDecimal: (oddsAmerican: string) => number;
	convertAmericanToFractional: (oddsAmerican: string) => string;
	convertDecimalOddsToFractional: (oddsDecimal: number) => string;
	convertDecimalToAmerican: (oddsDecimal: number) => string;
	convertFractionalToAmerican: (oddsFractional: string) => string;
	convertFractionalToDecimal: (fractionalOdds: string) => number;
};

export type BetslipMeta = {
	callbacksRef: MutableRefObject<Record<string, CallbackFn[]>>;
	oddsFormatIs: {
		American: boolean;
		Decimal: boolean;
		Fractional: boolean;
	};

	userOddsFormat: OddsFormat;
};

export type Bet = {
	bestOddsBookmakers: string;
	bestOddsDecimal: number;
	bestOddsFractional: string;
	bestOddsUs: number;
	betId: number;
	bettypeId: number;
	categoryGroupId: number;
	categoryGroupName: string;
	categoryId: number;
	disabled?: boolean;
	eventId: number;
	eventName: string;
	genericName: string;
	map: string;
	marketId: number;
	marketName: string;
	name: string;
	odds: Odds[];
	subeventId: number;
	subeventName: string;
	uiOptions: any;
};

export type Odds = {
	betIds?: number[];
	bookmakerBetId: string;
	bookmakerCode: string;
	bookmakerType: 'traditional' | 'exchange';
	clickoutUrl: string;
	eachWayTerms: string;
	index?: number;
	oddsDecimal: number;
	oddsFractional: string;
	oddsUs: string;
	suspended: boolean;
};

export type IntBetslipCookie = Record<'intBetslipCookie', string>;

export type BetslipState = {
	added: boolean;
	betIds: number[];
	betslip: Bet[];
	bookieOdds: BookieOdds[];
	cookie: IntBetslipCookie;
	count: number;
	loading: boolean;
	open: boolean;
	selectedBookie: BookieOdds | null;
	stake: number;
};

export type StateUtils = {
	addOrRemoveFromBetslip: (
		betArray: any[],
		matchBetIds?: any[],
		bookie?: string,
	) => void;
	changeStake: (stake: number) => void;
	fetchBetslipData: () => void;
	handleClearBetslip: () => void;
	handleClickoutToPlaceBet: (bookie: string, betIds: number[]) => void;
	removeBetFromBetslip: (betId: number, disabled?: boolean, bet?: Bet) => void;
	selectBookie: (idx: number) => void;
	storeBetslipData: (data: Bet[]) => void;
	toggleAdded: (val?: boolean) => void;
	toggleBetslip: (val?: boolean) => void;
};

export type BetslipProviderData = BetslipUtils &
	OddsUtils &
	BetslipMeta &
	BetslipState &
	StateUtils;
